import React from "react";
import { FaCoins } from "react-icons/fa";
import coffeeBean from "../../../Assets/icons/coffee_bean.png";
import teaLeaf from "../../../Assets/icons/tea_leaf.png";
import InfoTip from "../../atoms/InfoTip";

const PRODUCT_ICONS = {
  Çay: teaLeaf,
  "Türk Kahvesi": coffeeBean,
  "Filtre Kahve": coffeeBean,
  Espresso: coffeeBean,
};

// FirmCard'daki kota sütunuyla aynı ürün listesi ve sırası — diğer ürünler
// (eski Espresso alt ürünleri vb.) gösterilmez.
const QUOTA_PRODUCTS = ["Çay", "Türk Kahvesi", "Filtre Kahve", "Espresso"];

const fmt = (n) => (Number(n) || 0).toLocaleString("tr-TR");

/**
 * Bayi dashboard — Bayi Kotası, ürün bazında iki parça (#176):
 *   Bende                      = total        (havuz + kendi cihazlarımda kalan)
 *   Müşteri ve alt bayilerimde = subFirmQuota (ağaçtaki diğer firmaların havuz + cihaz kotası)
 * Büyük rakam ikisinin toplamıdır.
 *
 * Kaynak: getDealerOverview → data.quota (sözleşme v1.1: Workspace
 * docs/api-contracts/dealer-overview-quota.md). `dashboard.quota` izni
 * yoksa sunucu [] döner; sayfa kartı zaten render etmez.
 *
 * Kapsam seçicisinden bağımsızdır, üst durum kutuları gibi.
 */
const HINTS = {
  own: "Henüz kimseye dağıtmadığınız havuzunuz ile kendi cihazlarınızda kalan kotanın toplamı.",
  sub: "Alt bayilerinize ve müşterilerinize (alt bayilerinizin müşterileri dahil) gönderdiğiniz, şu an onların havuzunda ve cihazlarında duran kota.",
};

export default function QuotaSummaryCard({ quota, isLoading = false }) {
  const items = QUOTA_PRODUCTS.map((name) =>
    (quota || []).find((q) => q?.productName === name)
  ).filter(Boolean);

  if (!isLoading && items.length === 0) return null;

  return (
    <div className="mx-2 sm:mx-0 rounded-2xl bg-white border border-gray-200 shadow-sm p-3 sm:p-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="p-2 bg-indigo-50 border border-indigo-100 rounded-xl">
          <FaCoins className="w-4 h-4 text-indigo-600" />
        </div>
        <h2 className="text-base sm:text-lg font-SemiBold text-gray-800">
          Bayi Kotası
        </h2>
        <div className="ml-auto hidden sm:flex items-center gap-3 text-xs text-gray-500">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
            Bende
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-sky-500" />
            Müşteri ve alt bayilerimde
          </span>
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2 sm:gap-3">
          {QUOTA_PRODUCTS.map((name) => (
            <div
              key={name}
              className="rounded-xl border border-gray-100 bg-gray-50 h-[150px] animate-pulse"
            />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2 sm:gap-3">
          {items.map((item) => (
            <QuotaProductTile key={item.productName} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

function QuotaProductTile({ item }) {
  const own = Number(item.total) || 0;
  const sub = Number(item.subFirmQuota) || 0;
  const total = own + sub;

  // Havuzlar eksiye düşebilir (cihazlara havuzdan fazlası verilmiş, cihazlar
  // bağlandıkça geri toplanır). Çubukta eksi kısım 0 sayılır.
  const barBase = Math.max(own, 0) + Math.max(sub, 0);
  const ownPct = barBase > 0 ? (Math.max(own, 0) / barBase) * 100 : 0;
  const subPct = barBase > 0 ? (Math.max(sub, 0) / barBase) * 100 : 0;

  return (
    <div className="rounded-xl border border-gray-200 p-3 sm:p-4">
      <div className="flex items-center gap-2">
        {PRODUCT_ICONS[item.productName] && (
          <img
            src={PRODUCT_ICONS[item.productName]}
            alt=""
            className="w-5 h-5 object-contain"
          />
        )}
        <span className="text-sm font-Medium text-gray-600">
          {item.productName}
        </span>
      </div>

      <p
        className={`mt-1 text-2xl sm:text-3xl font-Bold tabular-nums ${
          total < 0 ? "text-red-600" : "text-gray-900"
        }`}
      >
        {fmt(total)}
      </p>

      <div className="mt-3 h-2 w-full rounded-full bg-gray-100 overflow-hidden flex">
        <div className="h-full bg-emerald-500" style={{ width: `${ownPct}%` }} />
        <div className="h-full bg-sky-500" style={{ width: `${subPct}%` }} />
      </div>

      <div className="mt-3 space-y-1.5 text-sm">
        <QuotaRow dotClass="bg-emerald-500" label="Bende" hint={HINTS.own} value={own} />
        <QuotaRow
          dotClass="bg-sky-500"
          label="Müşteri ve alt bayilerimde"
          hint={HINTS.sub}
          value={sub}
        />
      </div>
    </div>
  );
}

function QuotaRow({ dotClass, label, hint, value }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="flex items-center gap-1.5 text-gray-600 min-w-0">
        <span className={`w-2 h-2 rounded-full shrink-0 ${dotClass}`} />
        <span className="truncate">{label}</span>
        <InfoTip label={`${label} nedir?`}>{hint}</InfoTip>
      </span>
      <span
        className={`font-SemiBold tabular-nums shrink-0 ${
          value < 0 ? "text-red-600" : "text-gray-800"
        }`}
      >
        {fmt(value)}
      </span>
    </div>
  );
}
